import { useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, KeyRound, Link2Off, ShieldCheck } from 'lucide-react';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { OTP_UI } from '@/lib/constants/otp';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmailIcon } from '@/assets/icons/EmailIcon';
import { AuthHero } from '@/components/common/AuthHero';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { LinkStatusCard } from '@/components/common/LinkStatusCard';
import { ForgotPasswordCodeStep } from './ForgotPasswordCodeStep';
import { ForgotPasswordPasswordStep, type ForgotPasswordPasswordValues } from './ForgotPasswordPasswordStep';
import { useRequestPasswordReset, useCompletePasswordReset } from '@/features/auth/hooks/usePasswordReset';
import { useCountdown } from '@/hooks/useCountdown';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { journeySignInPath } from '@/lib/auth/post-auth-landing';

/**
 * The four states of the member reset flow (MYN-133 / FR-05b), all on one
 * route (`/forgot-password`) so the email stays in component state - a
 * separate route for the code step would have no email context. The former
 * `'sent'` interstitial was collapsed into the code step: submitting the
 * email lands directly on the OTP form, which carries the same neutral
 * acknowledgement as its subtitle plus the Resend control. `'code'` and
 * `'password'` split what used to be one combined `'reset'` step so the
 * password fields get a full-width, uncluttered screen of their own; moving
 * between them is a plain client-side transition (see
 * `handleContinueToPassword`), not a second request. `'failed'` is the
 * single non-revealing state for every OTP failure reason; there is no
 * separate `'done'` value because the terminal confirmation renders in place
 * of the `'password'` step's form once the mutation succeeds.
 */
export type ForgotPasswordStep = 'request' | 'code' | 'password' | 'failed';

const F = UI_STRINGS.FORGOT_PASSWORD;

const requestSchema = z.object({
  email: z.string().trim().min(1, F.EMAIL_REQUIRED).email(F.EMAIL_INVALID),
});
type RequestFormValues = z.infer<typeof requestSchema>;

const CODE_PATTERN = new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`);

interface HeroContent {
  badge: string;
  heading: ReactNode;
  subtitle?: ReactNode;
}

const ICON_SIZE = 16;

export function ForgotPasswordPage() {
  const [step, setStep] = useState<ForgotPasswordStep>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();
  const [resent, setResent] = useState(false);
  // Marks that the in-flight requestReset.mutate() call is a resend, not the
  // initial request - read (and cleared) inside onRequested below, so the
  // "a new code is on its way" confirmation is reported from the mutation's
  // own success path, never optimistically at the click site (BLOCKER:
  // a failed resend must not claim success).
  const isResendRef = useRef(false);

  // Cooldown starts (and restarts) whenever a code is actually (re)sent -
  // both the first request and every resend land on the same onRequested,
  // defined on the mutation so it survives `step` changing beneath it and so
  // a failed resend never reaches this callback at all.
  const cooldown = useCountdown(OTP_UI.RESEND_COOLDOWN_SECONDS);
  const requestReset = useRequestPasswordReset(() => {
    setStep('code');
    cooldown.reset();
    if (isResendRef.current) {
      isResendRef.current = false;
      setResent(true);
    }
  });
  // Every OTP failure reason (wrong, expired, already used, never issued)
  // arrives as the one OTP_INVALID code, so this is the only branch needed to
  // reach the single non-revealing failed state (security invariant: never
  // distinguish why).
  const completeReset = useCompletePasswordReset(() => setStep('failed'));

  const requestForm = useForm<RequestFormValues>({ resolver: zodResolver(requestSchema) });

  const onRequestSubmit = (values: RequestFormValues) => {
    setEmail(values.email);
    requestReset.mutate(values.email);
  };

  const handleResend = () => {
    isResendRef.current = true;
    requestReset.mutate(email);
  };

  /**
   * Advances from the code screen to the password screen. This is a plain
   * client-side step transition, not a request: `OtpService.verify()`
   * consumes the OTP challenge on success, so calling it here to check the
   * code early would use it up and leave nothing to authorise the password
   * set on submit. There is no verify-only endpoint, and there should not be
   * one - the flow is specified as a single `completeReset` request, fired
   * once from the password step. A wrong code is only ever discovered there,
   * where it collapses to the one non-revealing `failed` step (never routed
   * back here - that would reveal which of the four OTP failure reasons
   * occurred).
   */
  const handleContinueToPassword = () => {
    if (!CODE_PATTERN.test(code)) {
      setCodeError(F.CODE_REQUIRED);
      return;
    }
    setCodeError(undefined);
    setStep('password');
  };

  const onPasswordSubmit = (values: ForgotPasswordPasswordValues) => {
    completeReset.mutate({
      email,
      code,
      newPassword: values.newPassword,
      confirmNewPassword: values.confirmNewPassword,
    });
  };

  const handleRestart = () => {
    completeReset.reset();
    // ui-guidelines.md §12: reset the mutation when the dialog/step closes,
    // or a stale inline failure renders against the fresh request form -
    // handleRestart lands back on 'request' from 'failed', so a stale
    // requestReset error (e.g. from an earlier failed resend) must not
    // paint onto it.
    requestReset.reset();
    setCode('');
    setCodeError(undefined);
    setResent(false);
    isResendRef.current = false;
    cooldown.reset();
    setStep('request');
  };

  const handleUseDifferentEmail = () => {
    // Same stale-failure gap as handleRestart above: leaving 'code' for a
    // fresh 'request' form must not carry over a failed resend's error.
    requestReset.reset();
    setStep('request');
  };

  if (step === 'failed') {
    return (
      <LinkStatusCard
        icon={<Link2Off size={32} />}
        heading={F.FAILED_HEADING}
        body={F.FAILED_BODY}
        ctaLabel={F.FAILED_CTA}
        onCtaClick={handleRestart}
      />
    );
  }

  const HERO_BY_STEP: Record<Exclude<ForgotPasswordStep, 'failed'>, HeroContent> = {
    request: {
      badge: F.BADGE,
      heading: (
        <>
          {F.HEADING_PREFIX}
          <span className="text-green">{F.HEADING_HIGHLIGHT}</span>
        </>
      ),
      subtitle: F.SUBTITLE,
    },
    code: {
      badge: F.BADGE,
      heading: (
        <>
          {F.CODE_HEADING_PREFIX}
          <span className="text-green">{F.CODE_HEADING_HIGHLIGHT}</span>
        </>
      ),
      // Same neutral, non-revealing acknowledgement that used to live on the
      // deleted 'sent' interstitial - wording unchanged, now the subtitle for
      // the code step itself.
      subtitle: F.SENT_ACK,
    },
    password: {
      badge: F.RESET_BADGE,
      heading: (
        <>
          {F.RESET_HEADING_PREFIX}
          <span className="text-green">{F.RESET_HEADING_HIGHLIGHT}</span>
        </>
      ),
    },
  };
  const hero = HERO_BY_STEP[step];

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={hero.badge}
        heading={hero.heading}
        subtitle={hero.subtitle}
      />

      <div className="mt-10 flex w-full max-w-xl flex-col gap-6">
        {step === 'request' && (
          <form
            onSubmit={requestForm.handleSubmit(onRequestSubmit)}
            noValidate
            className="flex flex-col gap-6"
          >
            <SectionCard title={F.CARD_TITLE} subtitle={F.CARD_SUBTITLE} icon={<KeyRound size={20} />}>
              {requestReset.isError && (
                <p role="alert" className="mb-4 text-center text-sm text-danger">
                  {getApiErrorMessage(requestReset.error, F.REQUEST_ERROR)}
                </p>
              )}
              <Input
                {...requestForm.register('email')}
                label={F.EMAIL_LABEL}
                type="email"
                autoComplete="email"
                placeholder={F.EMAIL_PLACEHOLDER}
                icon={<EmailIcon />}
                error={requestForm.formState.errors.email?.message}
              />
            </SectionCard>

            {/*
             * Footer sits on the page background, not in a SectionCard. The card
             * treatment (p-8 plus shadow) added roughly 145px of chrome around
             * what is only a microcopy line and two controls, which read as an
             * oversized empty second card. This is the shipped InviteSignupPage
             * footer pattern, not a new one; VerifyEmailPage still uses the card
             * variant and is left alone as out of scope.
             */}
            <div className="flex flex-col gap-4">
              <ShieldNote>{F.ENCRYPTION_NOTE}</ShieldNote>
              <div className="flex items-center justify-between gap-6">
                <Link
                  to={journeySignInPath()}
                  className="inline-flex items-center gap-1.5 font-sans text-sm font-medium text-navy hover:text-dkgreen"
                >
                  <ArrowLeft size={ICON_SIZE} aria-hidden="true" />
                  {F.BACK_TO_LOGIN}
                </Link>
                <Button
                  type="submit"
                  variant="navy"
                  disabled={requestReset.isPending}
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
                >
                  {requestReset.isPending ? F.SUBMITTING : F.SUBMIT}
                  <ArrowRight size={18} aria-hidden="true" />
                </Button>
              </div>
            </div>
          </form>
        )}

        {step === 'code' && (
          <ForgotPasswordCodeStep
            code={code}
            onCodeChange={setCode}
            codeError={codeError}
            onContinue={handleContinueToPassword}
            onResend={handleResend}
            onUseDifferentEmail={handleUseDifferentEmail}
            isResendPending={requestReset.isPending}
            cooldownExpired={cooldown.expired}
            resendErrorMessage={
              requestReset.isError ? getApiErrorMessage(requestReset.error, F.REQUEST_ERROR) : undefined
            }
            resent={resent}
          />
        )}

        {step === 'password' &&
          (completeReset.isSuccess ? (
            <SectionCard title={F.DONE_HEADING} icon={<ShieldCheck size={20} />}>
              <p className="text-center text-sm text-subtext">{F.DONE_BODY}</p>
              <div className="mt-6 flex justify-center">
                <Button
                  asChild
                  variant="navy"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
                >
                  <Link to={journeySignInPath()}>{F.DONE_CTA}</Link>
                </Button>
              </div>
            </SectionCard>
          ) : (
            <ForgotPasswordPasswordStep
              onSubmit={onPasswordSubmit}
              isPending={completeReset.isPending}
              errorMessage={
                completeReset.isError ? getApiErrorMessage(completeReset.error, F.RESET_ERROR) : undefined
              }
            />
          ))}
      </div>
    </div>
  );
}
